import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { motion } from 'framer-motion';
import axios from 'axios';
import PhotoCompareSlider from '../components/PhotoCompareSlider';
import { GitCompare, Camera, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicComparePage() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPage();
  }, [slug]);

  const fetchPage = async () => {
    try {
      const res = await axios.get(`${API}/public/compare/${slug}`);
      setPage(res.data);
    } catch (err) {
      setError(err.response?.status === 404 ? 'Strona nie znaleziona' : 'Blad ladowania');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <GitCompare className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-2xl font-bold mb-2">Nie znaleziono</h2>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="max-w-5xl mx-auto px-6 py-8 text-center">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-3xl md:text-4xl font-bold mb-3">{page.title}</h1>
            {page.description && (
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">{page.description}</p>
            )}
            {page.photographer_name && (
              <div className="flex items-center justify-center gap-2 mt-4 text-sm text-muted-foreground">
                <Camera className="w-4 h-4" />
                <span>{page.photographer_name}</span>
              </div>
            )}
          </motion.div>
        </div>
      </header>

      {/* Pairs */}
      <main className="max-w-5xl mx-auto px-6 py-10 space-y-10">
        {(page.pairs || []).map((pair, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.15 }}
          >
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <div className="aspect-[16/9] bg-secondary flex items-center justify-center relative">
                  {/* If PhotoCompareSlider is available and we have image URLs, use it */}
                  {pair.before_url && pair.after_url ? (
                    <PhotoCompareSlider
                      beforeImage={pair.before_url}
                      afterImage={pair.after_url}
                    />
                  ) : (
                    <div className="flex items-center gap-8 text-muted-foreground">
                      <div className="text-center">
                        <div className="w-32 h-24 bg-secondary-foreground/5 rounded-lg flex items-center justify-center mb-2">
                          <span className="text-xs">BEFORE</span>
                        </div>
                        <span className="text-xs">{pair.before_photo_key}</span>
                      </div>
                      <GitCompare className="w-6 h-6" />
                      <div className="text-center">
                        <div className="w-32 h-24 bg-secondary-foreground/5 rounded-lg flex items-center justify-center mb-2">
                          <span className="text-xs">AFTER</span>
                        </div>
                        <span className="text-xs">{pair.after_photo_key}</span>
                      </div>
                    </div>
                  )}
                </div>
                {pair.caption && (
                  <div className="p-4 text-center">
                    <p className="text-sm text-muted-foreground">{pair.caption}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        ))}

        {(page.pairs || []).length === 0 && (
          <div className="text-center py-16 text-muted-foreground">
            <GitCompare className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Brak par zdjec</p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t bg-card py-6 text-center text-sm text-muted-foreground">
        <p>Powered by Focus Photo Pro</p>
      </footer>
    </div>
  );
}
